import type { LevelAssessmentResult } from "@shared/labs/level-assessment";
import { ArrowRight, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { rpcClient } from "@/lib/rpc";
import { ReviewPanel } from "../results/review-panel";
import { C, DM, levelLabel, UI } from "../results/ui-tokens";
import { initials, Muted } from "./ui";

interface FullEvaluation {
  evaluationId: string;
  modelId: string;
  disciplineId: string | null;
  candidateName: string;
  candidateSubtitle?: string;
  linkedinUrl: string | null;
  result: LevelAssessmentResult;
}

/**
 * Per-candidate review drawer for the evaluation-set table. Lazily fetches the
 * full persisted evaluation (the table only carries a compact projection) and
 * reuses the shared `ReviewPanel` for the per-competency agree/adjust — stamped
 * with the set id for provenance. "Ver avaliação completa" deep-links to the
 * standalone report at `/e/:id`.
 */
export function CandidateDrawer({
  evaluationId,
  setId,
  levelNames,
  onClose,
  onReviewed,
}: {
  evaluationId: string;
  setId: string;
  levelNames: Record<string, string>;
  onClose: () => void;
  /** Called after a review is saved so the table can refresh its review chips. */
  onReviewed: () => void;
}) {
  const [data, setData] = useState<FullEvaluation | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    (async () => {
      try {
        const res = (await rpcClient.labs.levelAssessmentEvaluationGet({
          evaluationId,
        })) as FullEvaluation;
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Erro inesperado");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [evaluationId]);

  const overall = data?.result.overallLevel ?? null;

  return (
    <>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop pattern, redundant with the explicit close button inside */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop dismiss is mouse-only; the drawer has its own focusable close button */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(31,27,23,0.28)",
          zIndex: 40,
        }}
      />
      <aside
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 560,
          maxWidth: "100vw",
          background: C.white,
          boxShadow: "-12px 0 32px -10px rgba(0,0,0,0.22)",
          display: "flex",
          flexDirection: "column",
          zIndex: 41,
        }}
      >
        {/* Head */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            padding: "18px 22px",
            borderBottom: `1px solid ${C.borderSoft}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 13, minWidth: 0 }}>
            <div
              style={{
                width: 46,
                height: 46,
                flex: "none",
                borderRadius: 12,
                background: C.ink,
                color: C.white,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ...UI,
                fontWeight: 700,
                fontSize: 15,
              }}
            >
              {initials(data?.candidateName ?? "?")}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
              <span style={{ ...DM, fontWeight: 700, fontSize: 20, color: C.ink }}>
                {data?.candidateName ?? "Carregando…"}
              </span>
              {data?.candidateSubtitle && (
                <span style={{ ...UI, fontSize: 13, color: C.ink50 }}>
                  {data.candidateSubtitle}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            style={{
              width: 32,
              height: 32,
              flex: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 999,
              border: 0,
              background: "transparent",
              color: C.ink50,
              cursor: "pointer",
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "20px 22px" }}>
          {error && <Muted>Falha ao carregar a avaliação: {error}</Muted>}
          {!data && !error && <Muted>Carregando avaliação…</Muted>}

          {data && (
            <>
              {/* Overall context */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  background: C.red5,
                  border: `1px solid ${C.red20}`,
                  borderRadius: 14,
                  padding: "14px 18px",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span
                    style={{
                      ...UI,
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: "0",
                      textTransform: "uppercase",
                      color: C.ink50,
                    }}
                  >
                    Nível estimado
                  </span>
                  <span
                    style={{
                      ...DM,
                      fontWeight: 800,
                      fontSize: 38,
                      lineHeight: 1,
                      letterSpacing: "-0.02em",
                      color: C.ink,
                    }}
                    title={levelLabel(overall, levelNames)}
                  >
                    {overall == null ? "—" : `L${overall}`}
                  </span>
                </div>
                <div style={{ flex: 1 }} />
                <Link
                  href={`/labs/level-assessment/e/${evaluationId}`}
                  style={{
                    ...UI,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    fontWeight: 600,
                    fontSize: 13,
                    color: C.red,
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Ver avaliação completa
                  <ArrowRight size={16} />
                </Link>
              </div>

              {/* Per-competency review (reuses the shared panel; set provenance) */}
              <div style={{ marginTop: 18 }}>
                <ReviewPanel
                  result={data.result}
                  reviewContext={{
                    modelId: data.modelId,
                    disciplineId: data.disciplineId,
                    evaluationId: data.evaluationId,
                    linkedinUrl: data.linkedinUrl ?? undefined,
                    candidateName: data.candidateName,
                    evaluationSetId: setId,
                  }}
                  onClose={onClose}
                  onSaved={() => onReviewed()}
                />
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
}
